// Started from shadcn/ui (new-york-v4) "tabs", restyled to Union. Switches between views of
// the same thing. "line" (default): page sections, underlined like the Staff app's tabs.
// "pill": a compact segmented control (Day / Week). Keyboard: arrows move, focus follows.

import { cn } from "@union/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { Tabs as TabsPrimitive } from "radix-ui";
import type * as React from "react";

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root data-slot="tabs" className={cn("flex flex-col gap-space-4", className)} {...props} />
  );
}

const listVariants = cva("inline-flex w-fit items-center", {
  variants: {
    variant: {
      line: "gap-space-5 border-b border-border",
      pill: "gap-space-1 rounded-pill bg-muted p-space-1",
    },
  },
  defaultVariants: { variant: "line" },
});

const triggerVariants = cva(
  "inline-flex items-center justify-center gap-space-2 whitespace-nowrap type-label transition-colors duration-150 disabled:pointer-events-none disabled:text-faint-foreground [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        line: "-mb-px h-tap-min border-b-2 border-transparent text-muted-foreground hover:text-foreground data-[state=active]:border-primary-text data-[state=active]:text-foreground",
        pill: "h-8 rounded-pill px-space-3 text-muted-foreground hover:text-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-raised",
      },
    },
    defaultVariants: { variant: "line" },
  },
);

type Variant = VariantProps<typeof listVariants>["variant"];

function TabsList({
  className,
  variant = "line",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & { variant?: Variant }) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(listVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  variant = "line",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & { variant?: Variant }) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(triggerVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content data-slot="tabs-content" className={cn("flex-1", className)} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
